/**
 * useAnchorSpy — B-2026-0088 Phase A2
 *
 * Generalised scroll-spy hook that watches all elements with a given data
 * attribute and reports the topmost intersecting one to the AdaptiveGuideContext
 * via `report({ activeSection })`.
 *
 * Extends `useSectionSpy` by adding a `MutationObserver` on `document.body`
 * (subtree:true, childList:true) that re-observes `[data-guide-section]` elements
 * as they mount or unmount. This fixes the observe-at-mount limitation of the
 * original `useSectionSpy` where dynamically-rendered panels were never picked up.
 *
 * Topmost-wins logic:
 *   - rootMargin '-15% 0px -50% 0px' (upper-middle reading zone)
 *   - When multiple sections intersect, the one with the smallest
 *     `boundingClientRect.top` (closest to top of viewport) wins.
 *
 * Test design note:
 *   MutationObserver is mocked as a no-op in test/setup.ts. Tests drive
 *   state via `report()` directly — the mutation path is not exercised in unit
 *   tests (consistent with IntersectionObserver mocking policy).
 *
 * Usage (in EvaluationResults — same as before, via useSectionSpy re-export):
 * ```tsx
 * useSectionSpy(report);
 * ```
 *
 * Usage (in StudioGuideOverlay — new for Phase A2):
 * ```tsx
 * useAnchorSpy(report, 'data-guide-section');
 * ```
 */

import { useEffect, useRef } from 'react';
import type { AdaptiveGuideState } from '@/contexts/AdaptiveGuideContext';

type ReportFn = (partial: Partial<AdaptiveGuideState>) => void;
type ObservedAttrName = 'data-guide-section' | 'data-field-id';

/**
 * Microtask-debounced function factory.
 * Returns a function that, when called, schedules `fn` to run as a microtask
 * at most once per microtask checkpoint (deduplicated via a pending flag).
 */
function makeMicrotaskDebounced(fn: () => void): () => void {
  let pending = false;
  return () => {
    if (pending) return;
    pending = true;
    queueMicrotask(() => {
      pending = false;
      fn();
    });
  };
}

export function useAnchorSpy(
  report: ReportFn,
  attrName: ObservedAttrName = 'data-guide-section',
): void {
  const intersectingRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Same Map instance for this effect run's whole lifetime (only ever
    // mutated via .set/.delete/.clear, never reassigned) — capturing it once
    // here and using `intersecting` everywhere below (including cleanup) is
    // behaviorally identical to reading `intersectingRef.current` each time.
    const intersecting = intersectingRef.current;

    // Compute the current winner (topmost intersecting section) and report.
    function pickWinner() {
      let winner: string | null = null;
      let winnerTop = Infinity;
      intersecting.forEach((top, key) => {
        if (top < winnerTop) {
          winnerTop = top;
          winner = key;
        }
      });
      report({ activeSection: winner });
    }

    // Create the IntersectionObserver.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          const key = el.getAttribute(attrName);
          if (!key) return;

          if (entry.isIntersecting) {
            intersecting.set(key, entry.boundingClientRect.top);
          } else {
            intersecting.delete(key);
          }
        });
        pickWinner();
      },
      {
        rootMargin: '-15% 0px -50% 0px',
        threshold: 0,
      },
    );

    // Sync: observe all matching elements, unobserve removed ones.
    function syncObservedElements() {
      const current = new Set(
        Array.from(document.querySelectorAll(`[${attrName}]`)),
      );

      // Unobserve elements no longer in DOM.
      intersecting.forEach((_, key) => {
        const still = document.querySelector(`[${attrName}="${key}"]`);
        if (!still) {
          intersecting.delete(key);
        }
      });

      // Observe all current elements (IntersectionObserver de-dupes internally).
      current.forEach((el) => observer.observe(el));

      pickWinner();
    }

    // Initial observe pass.
    syncObservedElements();

    // MutationObserver on body to catch dynamically-mounted/unmounted panels.
    // Microtask-debounced to batch rapid DOM mutations (e.g. re-renders).
    const debouncedSync = makeMicrotaskDebounced(syncObservedElements);

    const mutationObserver = new MutationObserver(debouncedSync);
    mutationObserver.observe(document.body, { subtree: true, childList: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      intersecting.clear();
    };
  // report is stable (useCallback in context). attrName is a string constant.

  }, [report, attrName]);
}
